import { useState } from 'react';
import {
  BookOpen,
  BarChart3,
  CalendarDays,
  List,
  Download,
  Target,
  GitCompareArrows,
} from 'lucide-react';
import logoBase64 from '@/assets/livro-caixa-logo.png?inline';
import { trpc } from '@/lib/trpc';
import type { LivroCaixaExportData } from '@/lib/excel/types';
import { Button } from '@/components/ui/Button';
import { LivroCaixaTable } from './livro-caixa/LivroCaixaTable';
import { BalanceteView } from './livro-caixa/BalanceteView';
import { SaldosDiariosView } from './livro-caixa/SaldosDiariosView';
import { GabaritoView } from './livro-caixa/GabaritoView';
import { PrevisaoOrcamentariaTable } from './livro-caixa/PrevisaoOrcamentariaTable';
import { ComparativoView } from './livro-caixa/ComparativoView';

type SubView = 'livro' | 'balancete' | 'saldos' | 'previsao' | 'comparativo' | 'gabarito';

interface TabLivroCaixaProps {
  eventoId: string;
}

export function TabLivroCaixa({ eventoId }: TabLivroCaixaProps) {
  const [activeView, setActiveView] = useState<SubView>('livro');

  const exportMutation = trpc.livroCaixa.exportExcel.useMutation({
    onSuccess: (data) => {
      downloadExcel(data);
    },
  });

  const downloadExcel = async (data: LivroCaixaExportData) => {
    try {
      // Import dinâmico: ExcelJS (~1MB) só é carregado ao exportar.
      const { buildLivroCaixaWorkbook } = await import('@/lib/excel/buildLivroCaixaWorkbook');
      const wb = buildLivroCaixaWorkbook(data, { logoBase64 });

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf as ArrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      // Remove caracteres inválidos de nome de arquivo (ex.: barras em "2025/2026").
      const nomeSeguro = data.evento.nome.replace(/[\\/:*?"<>|]/g, '-').trim() || 'Evento';
      const a = document.createElement('a');
      a.href = url;
      a.download = `Livro Caixa - ${nomeSeguro}.xlsx`;
      // Anexa ao DOM antes de clicar; alguns navegadores (Firefox) ignoram o
      // clique de um anchor solto. Revoga a URL em um tick posterior para não
      // abortar o download.
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      console.error('Falha ao gerar o Excel do Livro Caixa:', err);
      alert('Não foi possível gerar o arquivo Excel. Tente novamente.');
    }
  };

  const subViews = [
    { id: 'livro' as SubView, label: 'Livro Caixa', icon: BookOpen },
    { id: 'balancete' as SubView, label: 'Balancete', icon: BarChart3 },
    { id: 'saldos' as SubView, label: 'Saldos Diários', icon: CalendarDays },
    { id: 'previsao' as SubView, label: 'Previsão', icon: Target },
    { id: 'comparativo' as SubView, label: 'Comparativo', icon: GitCompareArrows },
    { id: 'gabarito' as SubView, label: 'Gabarito', icon: List },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
          {subViews.map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                activeView === view.id
                  ? 'text-primary-600 bg-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              <view.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{view.label}</span>
            </button>
          ))}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => exportMutation.mutate({ eventoId })}
          isLoading={exportMutation.isPending}
        >
          <Download className="mr-1.5 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      {/* Content */}
      {activeView === 'livro' && <LivroCaixaTable eventoId={eventoId} />}
      {activeView === 'balancete' && <BalanceteView eventoId={eventoId} />}
      {activeView === 'saldos' && <SaldosDiariosView eventoId={eventoId} />}
      {activeView === 'previsao' && <PrevisaoOrcamentariaTable eventoId={eventoId} />}
      {activeView === 'comparativo' && <ComparativoView eventoId={eventoId} />}
      {activeView === 'gabarito' && <GabaritoView eventoId={eventoId} />}
    </div>
  );
}
